import React from 'react';
import { isAbortError } from '../../../api/SopRulesAPI';
import { toast } from './toast';
import {
  pollImportRulesetJob,
  rulesetSummaryParts,
  startImportRulesetJob,
} from './ruleset-service';
import type { RulesetApplySummary, RulesetJobKind } from './ruleset-service';

export type RunningRulesetJob = {
  action: RulesetJobKind;
  name: string;
  processed: number;
  total: number;
};

/**
 * Start ruleset apply / turn-off / delete jobs and follow them to the end,
 * toasting the outcome. `onSettled` runs once a job ends (to reload the
 * rulesets), unless a newer job has replaced it.
 */
export const useRulesetJob = (
  databaseId: string | undefined,
  target: string,
  onSettled: () => Promise<void> | void,
) => {
  const [runningJob, setRunningJob] = React.useState<RunningRulesetJob | null>(
    null,
  );
  // Aborting only stops following the job; it keeps running on the server.
  const controllerRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => () => controllerRef.current?.abort(), []);

  /** Resolves true when the job finished successfully. */
  const run = async (
    rulesetId: string,
    action: RulesetJobKind,
    name: string,
  ): Promise<boolean> => {
    if (!databaseId) return false;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setRunningJob({ action, name, processed: 0, total: 0 });
    try {
      const started = await startImportRulesetJob(
        databaseId,
        rulesetId,
        action,
      );
      if (!started.success) {
        toast.error(started.error ?? 'The ruleset change could not be started');
        return false;
      }
      let summary: RulesetApplySummary | undefined = undefined;
      if (started.jobId) {
        const job = await pollImportRulesetJob(databaseId, started.jobId, {
          signal: controller.signal,
          onUpdate: (update) =>
            setRunningJob(
              (current) =>
                current && {
                  ...current,
                  processed: update.processedProducts,
                  total: update.totalProducts,
                },
            ),
        });
        summary = job.summary;
      }
      toast.success(
        action === 'apply'
          ? `"${name}" applied to ${target}${
              summary ? `: ${rulesetSummaryParts(summary).join(', ')}` : ''
            }`
          : action === 'deactivate'
          ? `"${name}" turned off; products were restored`
          : `"${name}" deleted`,
      );
      return true;
    } catch (err) {
      if (isAbortError(err)) return false;
      toast.error(
        err instanceof Error
          ? err.message
          : 'The ruleset change did not finish',
      );
      return false;
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setRunningJob(null);
        await onSettled();
      }
    }
  };

  return { runningJob, run };
};
